<script lang="ts" setup>
import { fetchVideoAddressById } from '~/apis/video/fetchVideoAddressById'

definePageMeta({
  layout: 'fullwidth'
})
const route = useRoute()
const router = useRouter()
const id = Number(route.params.id?.toString())
const paragraph = ref(Number(route.query.p?.toString() || 1))
const playerParagraph = ref(0) // 播放器内部当前分P
const videoPlayerRef = ref()
const widescreen = useState<boolean>('player_widescreen')
const videoAddress = ref<Video.VideoItem[]>([])

// 若检测到路由更改，则自动切换分P
watch(
  () => Number(route.query.p?.toString() || 1),
  (p) => {
    switchVideo(p)
  }
)

onMounted(() => {
  getVideoAddress()

  useEmitter().on('rewardSuccess', ({ resourceId, resourceType, count }) => {
    if (video.value && resourceId === video.value.id && resourceType === 1) {
      video.value.reward_count += count
    }
  })

  useEmitter().on(
    'favoriteSuccess',
    ({ resourceId, resourceType, isFavorite }) => {
      if (resourceId === video.value?.id && resourceType === 1) {
        if (isFavorite) {
          video.value.favorite_count++
        } else {
          video.value.favorite_count--
        }
      }
    }
  )
})
const { data: video } = await useAsyncData(`video:${id}`, () =>
  fetchVideoById(id, true)
)
const { data: hotList } = useAsyncData(() => fetchHotPosts(5), {
  lazy: true,
  server: false,
  default: () => [] as Post.Info[]
})

const { data: relatedList } = useAsyncData(
  () => fetchRelatedPosts(id, 1, -1, 20),
  {
    lazy: true,
    server: false,
    default: () => [] as Post.Info[]
  }
)

if (!video.value) {
  throw createError({ statusCode: 404, message: '视频不见了' })
}

const keywords = ref(video.value.tags)

const description = parseDescription(htmlToText(video.value.content))
// 搜索引擎不允许0的出现
const duration = formatVideoDuration(video.value.duration || 10)
const uploadDate = new Date(video.value.created_at * 1000).toISOString()
const embedUrl = `https://www.mfuns.net/embed/${id}`
const ldJson = {
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: video.value.title,
  description,
  thumbnailUrl: [video.value.cover],
  uploadDate,
  duration,
  embedUrl,
  interactionStatistic: {
    '@type': 'InteractionCounter',
    interactionType: 'https://schema.org/WatchAction',
    userInteractionCount: video.value.view_count
  }
}

if (video.value) {
  let meta = [
    {
      name: 'description',
      content: parseDescription(description)
    },
    {
      name: 'keywords',
      content: keywords.value.join(',')
    }
  ]
  if (video.value.noindex) {
    meta.push({
      name: 'robots',
      content: 'noindex'
    })
  }
  useHead({
    title: parseTitle(video.value.title),
    link: [
      {
        rel: 'canonical',
        href: `https://www.mfuns.net/video/${id}`
      },
      {
        rel: 'alternate',
        media: 'only screen and (max-width: 640px)',
        href: `https://m.mfuns.net/video/${id}`
      }
    ],
    meta: meta,
    script: [
      {
        src: 'https://resource.mfuns.net/js/mfunsplayer/web/1.0.2/mfunsPlayer.min.umd.js'
      },
      {
        src: 'https://resource.mfuns.net/js/echarts/6.0.0/echarts.min.js',
        defer: true
      },
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(ldJson)
      }
    ],
    htmlAttrs: {
      itemprop: 'video',
      itemscope: true,
      itemType: 'http://schema.org/VideoObject'
    } as any
  })
}

useSeoMeta({
  title: parseTitle(video.value.title),
  ogSiteName: '喵御宅',
  ogUrl: `https://www.mfuns.net/video/${id}`,
  ogTitle: video.value.title,
  ogImage: video.value.cover,
  ogImageWidth: 1280,
  ogImageHeight: 720,
  ogDescription: description,
  ogType: 'video.other',
  ogVideoUrl: embedUrl,
  ogVideoSecureUrl: embedUrl,
  ogVideoType: 'video/mp4',
  ogVideoWidth: 1280,
  ogVideoHeight: 720
})

const screen = useScreen()

async function getVideoAddress() {
  const res = await fetchVideoAddressById(id)
  videoAddress.value = res.videos || []
  console.log('视频地址列表', videoAddress.value)
}

function switchVideo(p: number) {
  // 检测是否有重复调用该函数
  if (p != playerParagraph.value) {
    videoPlayerRef.value?.switchVideo(p - 1)
    playerParagraph.value = p
  }
  paragraph.value = p
}
</script>

<template>
  <div
    v-if="video"
    class="m-video"
    :class="{
      widescreen: widescreen || screen.xs || screen.sm,
      mobile: screen.xs || screen.sm
    }"
  >
    <Head>
      <link itemprop="url" :href="`https://www.mfuns.net/video/${id}`" />
      <Meta itemprop="name" name="title" :content="video.title" />
      <Meta itemprop="description" name="description" :content="description" />
      <Meta itemprop="requiresSubscription" content="false" />
      <Meta itemprop="duration" :content="duration" />
      <link itemprop="thumbnailUrl" :href="video.cover" />
      <Meta itemprop="uploadDate" :content="uploadDate" />
      <Meta itemprop="playerType" content="HTML5 Flash" />
      <Meta itemprop="width" content="1280" />
      <Meta itemprop="height" content="720" />
      <Meta itemprop="datePublished" :content="uploadDate" />
      <Meta itemprop="uploadDate" :content="uploadDate" />
      <Meta
        itemprop="embedUrl"
        :content="`https://www.mfuns.net/embed/${id}`"
      />
    </Head>
    <span itemprop="author" itemscope itemtype="http://schema.org/Person">
      <link
        itemprop="url"
        :href="`http://www.mfuns.net/member/${video.user.id}`"
      />
      <link itemprop="name" :content="video.user.name" />
    </span>
    <span
      itemprop="thumbnail"
      itemscope
      itemtype="http://schema.org/ImageObject"
    >
      <link itemprop="url" :href="video.cover" />
      <meta itemprop="width" content="1280" />
      <meta itemprop="height" content="720" />
    </span>
    <div class="m-video__wrapper">
      <main class="m-video__main">
        <MVideoPlayer
          ref="videoPlayerRef"
          v-model:paragraph="paragraph"
          :video-id="id"
          :video-list="videoAddress"
          danmaku-ele-id="danmakuList"
          @update:paragraph="
            (p) => {
              playerParagraph = p
              router.push({
                path: `/video/${id}`,
                query: {
                  p: p
                }
              })
            }
          "
        />
      </main>
      <div class="m-video__content">
        <header class="m-video__header">
          <m-container padding>
            <div class="m-video__title">
              <h1>{{ video.title }}</h1>
            </div>
            <div class="m-video__info">
              <div class="m-video__info-item">
                <a
                  :href="`/member/${video.user.id}`"
                  target="_blank"
                  rel="author"
                  class="link"
                >
                  {{ video.user.name }}
                </a>
              </div>
              <div class="m-video__info-item">
                <m-icon icon="lucide-chevron-right">
                  <template #start>
                    <m-link
                      link
                      :title="video.category.parent_category?.name"
                      :to="`/category/${video.category.parent_category?.id}`"
                    >
                      {{ video.category.parent_category?.name }}
                    </m-link>
                  </template>
                  <m-link
                    link
                    :to="`/category/${video.category.id}`"
                    :title="video.category.name"
                  >
                    {{ video.category.name }}
                  </m-link>
                </m-icon>
              </div>
              <time
                :datetime="dayjs(video.created_at * 1000).format()"
                :title="
                  dayjs(video.created_at * 1000)
                    .locale('zh-cn')
                    .format('ddd MMM DD YYYY HH:mm:ss [GMT]ZZ (中国标准时间)')
                "
                class="time m-video__info-item"
              >
                {{ dayjs(video.created_at * 1000).format('YYYY-MM-DD HH:mm') }}
              </time>
              <span class="m-video__info-item">
                <m-icon icon="lucide-eye" />
                <span>{{ pipe.num(video.view_count) }}</span>
              </span>
              <span v-if="video.copyright === 2" class="m-video__info-item">
                <m-icon icon="lucide-copyright" />
                <span>原创声明，未经作者许可禁止转载</span>
              </span>
            </div>
            <div class="m-video__desc">
              <m-text-ellipsis :html="xss(video.content)" :rows="3" />
            </div>
            <div v-if="video.tags.length" class="m-video__tags">
              <m-tag v-for="tag in video.tags" :key="tag" link :label="tag" />
            </div>
            <div
              v-if="screen.xs || screen.sm"
              v-show="false"
              id="danmakuList"
              class="m-video__danmaku"
            ></div>
          </m-container>
        </header>
        <m-container id="comment">
          <m-comment-list :area-id="video.comment_area_id" />
        </m-container>
      </div>
      <div v-if="!screen.xs || screen.sm" class="m-video__side">
        <m-container class="m-video__author" padding>
          <m-author :user="video.user" size="large" follow :row="2" />
        </m-container>
        <m-container title="弹幕列表">
          <div id="danmakuList" class="m-video__danmaku"></div>
        </m-container>
        <m-container v-if="videoAddress.length > 1" title="播放列表">
          <div class="m-video__playlist">
            <NuxtLink
              v-for="(v, i) in videoAddress"
              :key="i + 1"
              no-prefetch
              :to="{
                path: `/video/${id}`,
                query: {
                  p: i + 1
                }
              }"
              class="m-video__playlist-item"
              :class="{ 'm-video__playlist-item--active': i + 1 === paragraph }"
              @click="switchVideo(i + 1)"
            >
              <span>P{{ i + 1 }}</span>
              <span>{{ v.title }}</span>
            </NuxtLink>
          </div>
        </m-container>
        <m-container title="热门内容" padding>
          <m-space vertical gap="12px">
            <m-post-card-leaderboard
              v-for="(p, i) in hotList"
              :key="p.id"
              :post="p"
              :rank="i + 1"
            />
          </m-space>
        </m-container>
        <m-container title="相关推荐" padding>
          <m-space vertical gap="12px">
            <m-post-card-leaderboard
              v-for="p in relatedList"
              :key="p.id"
              :post="p"
            />
          </m-space>
        </m-container>
      </div>
    </div>
    <client-only>
      <video-side-action v-if="!screen.xs || screen.sm" :video="video" />
    </client-only>
  </div>
</template>

<style lang="scss" scoped>
.m-video {
  &__header {
    display: flex;
    flex-direction: column;
    background-color: #fff;
    margin-bottom: 12px;
    border-radius: 8px;
  }
  &__danmaku {
    height: 300px;
    min-height: 300px;
  }
  &__wrapper {
    display: grid;
    grid-template-columns: auto min(30%, 360px);
    grid-template-rows: auto 1fr;
    grid-column-gap: 12px;
    grid-row-gap: 12px;
    align-content: flex-start;
  }

  &.mobile &__main {
    padding-top: 0;
  }

  &.mobile &__wrapper {
    display: flex;
    flex-direction: column;
  }

  &.mobile {
    padding: 0;
    background-color: #fff;
    h1 {
      font-size: 18px;
    }
    :deep(.m-container__body) {
      padding: 0 !important;
    }
    .m-video__content {
      padding: 0 16px;
    }
    :deep(.mfunsPlayer-footBar) {
      border: none;
      padding: 0 16px;
    }
    :deep(.mfunsPlayer-footBar .input_box) {
      display: none;
    }
    :deep(.mfunsPlayer-video-danmaku-details) {
      margin-right: auto;
    }
    :deep(.mfunsPlayer-video-danmaku-root) {
      flex: 0;
    }
    :deep(.mfunsPlayer-controller-right) {
      display: none;
    }
  }

  &__tags {
    margin-top: 12px;
  }
  &__main {
    grid-area: 1 / 1 / 2 / 2;
    padding-top: 20px;
    width: 100%;
    height: fit-content;
  }
  &__content {
    grid-area: 2 / 1 / 3 / 2;
  }
  &__side {
    grid-area: 1 / 2 / 3 / 3;
    margin-top: 20px;
    > * {
      margin-bottom: 12px;
    }
  }
  &__title {
    h1 {
      font-size: 22px;
      overflow: hidden;
      text-overflow: ellipsis;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      display: -webkit-box;
      -webkit-box-orient: vertical;
    }
  }
  &__info {
    margin: 12px 0;
    display: flex;
    flex-wrap: wrap;
    color: var(--color-gray-400);
    &-item {
      margin-right: 16px;
      cursor: default;
      display: flex;
      align-items: center;
      .m-icon {
        margin-right: 4px;
      }
    }
  }
  &__author {
    width: 100%;
  }
  &__playlist {
    max-height: 300px;
    padding: 12px 30px;
    overflow-y: auto;
    &-item {
      display: block;
      text-decoration: none;
      color: var(--color-gray-700);
      padding: 4px 8px;
      border-radius: 4px;
      margin: 4px 0;
      > span {
        margin-right: 12px;
      }
      &:hover {
        background-color: var(--color-gray-200);
      }
      &--active {
        background-color: var(--color-gray-100);
        color: var(--color-primary);
      }
    }
  }
}
.m-video.widescreen {
  .m-video {
    &__main {
      grid-area: 1 / 1 / 2 / 3;
    }
    &__content {
      grid-area: 2 / 1 / 3 / 2;
    }
    &__side {
      grid-area: 2 / 2 / 3 / 3;
      margin-top: 0;
    }
  }
}
@media screen and (max-width: 600px) {
  .m-video {
    &__title {
      font-size: 22px;
    }
  }
}
@media screen and (min-width: 600px) and (max-width: 960px) {
  .m-video {
    &__title {
      font-size: 22px;
    }
  }
}
</style>
